import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { auth } from '../../config/firebase';
import useAdminStore from '../../store/adminStore';
import { SalonInvoiceProfile } from '../../services/peInvoiceFirebaseService';
import { usePEInvoiceStore } from '../../store/usePEInvoiceStore';
import { isValidIban, normalizeIban } from '../../utils/pe-papers/ibanValidation';
import { getPeInvoiceTaxFromAppStore } from '../../utils/pe-papers/peInvoiceSenderFromAppStore';

export interface PEInvoiceSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Nach erfolgreichem Speichern (z. B. Vorschau öffnen) */
  onSaved?: () => void;
  /** Wenn true: IBAN/BIC als Pflicht hervorheben (z. B. vor Rechnungs-PDF) */
  requireBankForInvoice?: boolean;
}

const inputClass =
  'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-3 py-2 text-sm placeholder:text-pe-muted dark:placeholder:text-slate-500 focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const labelClass =
  'mb-1 block text-[10px] font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400';

const PEInvoiceSettingsModal: React.FC<PEInvoiceSettingsModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  requireBankForInvoice = false,
}) => {
  const profile = usePEInvoiceStore((s) => s.profile);
  const isSaving = usePEInvoiceStore((s) => s.isSaving);
  const updateProfile = usePEInvoiceStore((s) => s.updateProfile);

  const [localState, setLocalState] = useState<SalonInvoiceProfile>(profile);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (isOpen) {
      const fromStamm = getPeInvoiceTaxFromAppStore();
      setLocalState({
        ...profile,
        taxNumber: profile.taxNumber || fromStamm.taxNumber,
        vatId: profile.vatId || fromStamm.vatId,
      });
      setSaveError('');
    }
  }, [isOpen, profile]);

  if (!isOpen) return null;

  const mandantId =
    useAdminStore.getState().impersonatedClientId || auth.currentUser?.uid || '';

  const patch = <K extends keyof SalonInvoiceProfile>(key: K, value: SalonInvoiceProfile[K]) => {
    setLocalState((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!mandantId) return;
    const normalized: SalonInvoiceProfile = {
      ...localState,
      iban: normalizeIban(localState.iban || ''),
      bic: (localState.bic || '').replace(/\s+/g, '').toUpperCase(),
      accountHolder: (localState.accountHolder || '').trim(),
      taxNumber: (localState.taxNumber || '').trim(),
      vatId: (localState.vatId || '').trim(),
    };
    if (requireBankForInvoice || normalized.iban) {
      if (!isValidIban(normalized.iban)) {
        setSaveError(
          'Die IBAN ist ungültig (Prüfziffer). Bitte echte Bankdaten eintragen — Fantasie-IBANs werden abgelehnt.'
        );
        return;
      }
    }
    setSaveError('');
    await updateProfile(mandantId, normalized);
    if (onSaved) onSaved();
    else onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100002] flex items-center justify-center bg-stone-900/70 dark:bg-black/90 p-4 backdrop-blur-md font-sans"
    >
      <div
        className="relative w-full max-w-lg rounded-2xl border border-[#E6C299]/30 bg-pe-card dark:bg-[#111] text-pe-text dark:text-slate-50 shadow-[0_0_40px_rgba(230,194,153,0.1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] p-2 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white hover:bg-pe-hover dark:hover:bg-[#222] transition-colors"
          aria-label="Schließen"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="border-b border-pe-border dark:border-[#222] bg-pe-elevated dark:bg-[#111622] px-6 py-5 pr-14">
          <h3 className="text-lg font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]">
            Salon-Bankdaten & Einstellungen
          </h3>
          <p className="mt-2 text-xs text-pe-muted dark:text-slate-400">
            Einmalig hinterlegen — speichert in Firebase, gilt für spätere Rechnungen, jederzeit
            änderbar.
          </p>
          {requireBankForInvoice ? (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
              IBAN ist Pflicht für die Vorschau. Rechnungssteller-Name kommt aus den Stammdaten,
              kann hier aber für GmbH &amp; Co. angepasst werden.
            </p>
          ) : null}
        </div>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-5">
          <label className="block">
            <span className={labelClass}>
              Rechnungssteller / Firmenname (aus Stammdaten, anpassbar)
            </span>
            <input
              className={inputClass}
              value={localState.accountHolder}
              onChange={(e) => patch('accountHolder', e.target.value)}
              placeholder="z. B. Muster Salon GmbH"
            />
          </label>
          <label className="block">
            <span className={labelClass}>Kreditinstitut / Bankname</span>
            <input
              className={inputClass}
              value={localState.bankName}
              onChange={(e) => patch('bankName', e.target.value)}
            />
          </label>
          <label className="block">
            <span className={labelClass}>IBAN *</span>
            <input
              className={inputClass}
              value={localState.iban}
              onChange={(e) => patch('iban', e.target.value)}
              placeholder="DE89…"
              autoFocus={requireBankForInvoice}
            />
          </label>
          <label className="block">
            <span className={labelClass}>BIC (empfohlen)</span>
            <input
              className={inputClass}
              value={localState.bic}
              onChange={(e) => patch('bic', e.target.value)}
            />
          </label>
          <label className="block">
            <span className={labelClass}>Ihre Steuernummer (Absender, E-Rechnung)</span>
            <input
              className={inputClass}
              value={localState.taxNumber}
              onChange={(e) => patch('taxNumber', e.target.value)}
              placeholder="z. B. 123/456/78901"
            />
          </label>
          <label className="block">
            <span className={labelClass}>Ihre USt-IdNr. (Absender, E-Rechnung)</span>
            <input
              className={inputClass}
              value={localState.vatId || ''}
              onChange={(e) => patch('vatId', e.target.value)}
              placeholder="z. B. DE123456789"
            />
          </label>
          <p className="text-[10px] text-pe-muted dark:text-slate-500 -mt-2">
            Ihre Nummern — nicht die des Kunden. Werden in Firebase gespeichert; aus Stammdaten
            vorausgefüllt, falls dort hinterlegt.
          </p>
          <label className="block">
            <span className={labelClass}>Rechnungsnummer-Präfix</span>
            <input
              className={inputClass}
              value={localState.invoicePrefix}
              onChange={(e) => patch('invoicePrefix', e.target.value)}
              placeholder="RE-"
            />
          </label>
          <label className="block">
            <span className={labelClass}>Standard-Zahlungsziel (Tage)</span>
            <input
              type="number"
              min={0}
              className={inputClass}
              value={localState.defaultPaymentTermsDays}
              onChange={(e) =>
                patch('defaultPaymentTermsDays', Number(e.target.value) || 0)
              }
            />
          </label>

          <div className="border-t border-pe-border dark:border-[#333] pt-4 mt-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]/80 mb-3">
              Absender- &amp; Kontaktdaten (für E-Rechnung)
            </p>
            <div className="space-y-4">
              <label className="block">
                <span className={labelClass}>Straße &amp; Hausnummer</span>
                <input
                  className={inputClass}
                  value={localState.senderStreet || ''}
                  onChange={(e) => patch('senderStreet', e.target.value)}
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelClass}>PLZ</span>
                  <input
                    className={inputClass}
                    value={localState.senderZip || ''}
                    onChange={(e) => patch('senderZip', e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className={labelClass}>Ort</span>
                  <input
                    className={inputClass}
                    value={localState.senderCity || ''}
                    onChange={(e) => patch('senderCity', e.target.value)}
                  />
                </label>
              </div>
              <label className="block">
                <span className={labelClass}>Land</span>
                <input
                  className={inputClass}
                  value={localState.senderCountry || 'DE'}
                  onChange={(e) => patch('senderCountry', e.target.value.toUpperCase())}
                  placeholder="DE"
                />
              </label>
              <label className="block">
                <span className={labelClass}>Rechnungs-E-Mail</span>
                <input
                  type="email"
                  className={inputClass}
                  value={localState.senderEmail || ''}
                  onChange={(e) => patch('senderEmail', e.target.value)}
                />
              </label>
              <label className="block">
                <span className={labelClass}>Telefonnummer</span>
                <input
                  className={inputClass}
                  value={localState.senderPhone || ''}
                  onChange={(e) => patch('senderPhone', e.target.value)}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-pe-border dark:border-[#222] bg-pe-elevated dark:bg-[#111622] px-6 py-4">
          {saveError ? (
            <p className="text-xs text-rose-400">{saveError}</p>
          ) : null}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl border border-pe-border-strong dark:border-[#444] bg-transparent px-4 py-3 text-xs font-bold uppercase tracking-widest text-pe-muted dark:text-gray-300 hover:bg-pe-hover dark:hover:bg-[#222] transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="button"
              disabled={isSaving || !mandantId}
              onClick={() => void handleSave()}
              className="flex-1 rounded-xl bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white px-4 py-3 text-xs font-bold uppercase tracking-widest text-black transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Speichern…' : 'In Firebase speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PEInvoiceSettingsModal;
